import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { LinkedResourceType } from 'gen/api';
import { NavigationPanelState } from '../context/OfficeActionContext/OfficeActionContext';
import { TOfficeActionStatus } from '../types';

type ICurrentResource = { id: string; title: string; type: LinkedResourceType };

export enum OfficeActionSideDrawer {
  INPUTS = 'files',
  RESPOND = 'respond',
  SEARCH = 'search',
}

interface IOfficeActionState {
  projectId?: string;
  currentResource?: ICurrentResource;
  currentResources: ICurrentResource[];
  navigationPanelState: NavigationPanelState;
  openDrawer: OfficeActionSideDrawer | null;
  showBackButton: boolean;
  isDetectingDocuments: boolean;
  status?: TOfficeActionStatus;
  isNewOfficeAction: boolean;
}

const initialState: IOfficeActionState = {
  projectId: '',
  currentResource: undefined,
  currentResources: [],
  navigationPanelState: NavigationPanelState.APPLICATION_INPUT,
  openDrawer: OfficeActionSideDrawer.INPUTS,
  showBackButton: false,
  isDetectingDocuments: false,
  status: undefined,
  isNewOfficeAction: true,
};

const officeActionSlice = createSlice({
  name: 'officeAction',
  initialState,
  reducers: {
    setProjectId(state, action: PayloadAction<{ projectId: string }>) {
      state.projectId = action.payload.projectId;
    },

    setNavigationPanelState(state, action: PayloadAction<{ navigationPanelState: NavigationPanelState }>) {
      state.navigationPanelState = action.payload.navigationPanelState;
    },

    setOpenDrawer(state, action: PayloadAction<{ openDrawer: OfficeActionSideDrawer | null }>) {
      state.openDrawer = action.payload.openDrawer;
    },

    setShowBackButton(state, action: PayloadAction<{ showBackButton: boolean }>) {
      state.showBackButton = action.payload.showBackButton;
    },

    setIsDetectingDocuments(state, action: PayloadAction<{ isDetectingDocuments: boolean }>) {
      state.isDetectingDocuments = action.payload.isDetectingDocuments;
    },

    setCurrentResource(state, action: PayloadAction<{ resource: ICurrentResource | undefined }>) {
      state.currentResource = action.payload.resource;
    },

    addCurrentResource(state, action: PayloadAction<ICurrentResource>) {
      if (state.currentResources.some(r => r.id === action.payload.id)) return;
      const existingType = state.currentResources[0]?.type;
      const base = existingType && existingType !== action.payload.type ? [] : state.currentResources;
      state.currentResources = [...base, action.payload];
    },

    removeCurrentResource(state, action: PayloadAction<string>) {
      state.currentResources = state.currentResources.filter(r => r.id !== action.payload);
    },

    clearCurrentResources(state) {
      state.currentResources = [];
    },

    setCurrentResources(state, action: PayloadAction<ICurrentResource[]>) {
      state.currentResources = action.payload;
    },

    setStatus(state, action: PayloadAction<{ status: TOfficeActionStatus | undefined }>) {
      state.status = action.payload.status;
    },

    setIsNewOfficeAction(state, action: PayloadAction<{ isNewOfficeAction: boolean }>) {
      state.isNewOfficeAction = action.payload.isNewOfficeAction;
    },

    resetOfficeAction() {
      return initialState;
    },
  },
});

export default officeActionSlice.reducer;
export const officeActionActions = officeActionSlice.actions;
